/**
 * Task Card Component
 * Expandable card showing task details and subtasks
 * Matches AutoMatchReferrals card styling
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Edit2, Trash2, Calendar, User, CheckCircle, Circle, X } from 'lucide-react';
import {
  Task,
  Subtask,
  TaskStatus,
  tasksAPI,
  STATUS_LABELS,
  STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  TASK_TYPE_LABELS
} from '@/services/api/tasks';
import { formatDistance } from 'date-fns';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { ItemZoneNote } from '@/components/ui/ItemZoneNote';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onDeleted: () => void;
  onRefresh: () => void;
}

export default function TaskCard({ task, onEdit, onDeleted, onRefresh }: TaskCardProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  const [subtasks, setSubtasks] = useState<Subtask[]>(task.subtasks || []);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const loadSubtasks = async () => {
    if (subtasks.length === 0 && task.id) {
      setLoadingSubtasks(true);
      try {
        const fullTask = await tasksAPI.getTask(task.id);
        setSubtasks(fullTask.subtasks || []);
      } catch (error) {
        console.error('Error loading subtasks:', error);
      } finally {
        setLoadingSubtasks(false);
      }
    }
  };

  const handleToggleExpand = async () => {
    if (!isExpanded && (task.total_subtasks || 0) > 0) {
      await loadSubtasks();
    }
    setIsExpanded(!isExpanded);
  };

  const handleToggleSubtask = async (subtask: Subtask) => {
    if (!subtask.id) return;

    try {
      await tasksAPI.updateSubtask(subtask.id, {
        is_completed: !subtask.is_completed
      });

      // Update local state
      setSubtasks(subtasks.map(s =>
        s.id === subtask.id
          ? { ...s, is_completed: !s.is_completed, completed_at: !s.is_completed ? new Date().toISOString() : undefined }
          : s
      ));

      // Refresh parent to update progress
      onRefresh();
    } catch (error) {
      console.error('Error toggling subtask:', error);
      alert('Failed to update subtask');
    }
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (!task.id || newStatus === task.status) return;

    setUpdatingStatus(true);
    try {
      await tasksAPI.updateTask(task.id, { status: newStatus });
      onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!task.id) return;

    const confirmMessage = task.is_archived
      ? 'Permanently delete this task?'
      : 'Archive this task? You can restore it later from Archived tasks.';

    if (!confirm(confirmMessage)) return;

    try {
      await tasksAPI.deleteTask(task.id, task.is_archived);
      onDeleted();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  // Format deadline
  const getDeadlineDisplay = () => {
    if (!task.deadline && !task.due_at) return null;

    // TRUS-697: a due time is an instant; a bare deadline is a date.
    const deadline = new Date(task.due_at || (task.deadline as string));
    const now = new Date();
    const isOverdue = deadline < now && task.status !== 'completed';

    return {
      text: formatDistance(deadline, now, { addSuffix: true }),
      isOverdue,
      className: isOverdue ? 'text-red-600' : 'text-gray-600'
    };
  };

  const deadline = getDeadlineDisplay();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
      {/* Card Header - Always Visible */}
      <div className="p-4 md:p-6">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Task Info */}
          <div className="flex-1 min-w-0">
            {/* Title and Type */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white break-words">
                {task.title}
              </h3>
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                {TASK_TYPE_LABELS[task.task_type]}
              </span>
            </div>

            {/* Status and Priority Badges */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {/* Status Dropdown */}
              <select
                value={task.status}
                onChange={(e) => handleStatusChange(e.target.value as TaskStatus)}
                disabled={updatingStatus}
                className={`px-3 py-1 text-xs font-medium rounded-lg border appearance-none cursor-pointer min-h-[32px] ${STATUS_COLORS[task.status]} ${updatingStatus ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <span className={`px-3 py-1 text-xs font-medium rounded-lg ${PRIORITY_COLORS[task.priority]}`}>
                {PRIORITY_LABELS[task.priority]}
              </span>
              {task.is_overdue && (
                <span className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-lg">
                  OVERDUE
                </span>
              )}
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
              {deadline && (
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span className={deadline.className}>{deadline.text}</span>
                  {task.due_at && (
                    <span data-testid="task-due-at">
                      {fmt.dateTime(task.due_at)}{' '}
                      <ItemZoneNote value={task.due_at} itemTimeZone={task.timezone} kind="task" />
                    </span>
                  )}
                </div>
              )}

              {task.assigned_to_name && (
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Assigned to {task.assigned_to_name}</span>
                </div>
              )}

              {(task.total_subtasks || 0) > 0 && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    {task.completed_subtasks}/{task.total_subtasks} subtasks completed
                  </span>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {(task.total_subtasks || 0) > 0 && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-teal-600 h-2 rounded-full transition-all"
                    style={{ width: `${task.progress_percentage}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{task.progress_percentage}% Complete</p>
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(task)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('tasks.edit_task', 'Edit task')}
            >
              <Edit2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>

            <button
              onClick={handleDelete}
              className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('tasks.delete_task', 'Delete task')}
            >
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            </button>

            {(task.total_subtasks || 0) > 0 && (
              <button
                onClick={handleToggleExpand}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Description (if present) */}
        {task.description && (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
            {task.description}
          </p>
        )}
      </div>

      {/* Expanded Section - Subtasks */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-4 md:p-6">
          {loadingSubtasks ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : subtasks.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('tasks.subtasks', 'Subtasks')}</h4>
              {subtasks.map((subtask) => (
                <div
                  key={subtask.id}
                  className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-teal-300 dark:hover:border-teal-500 transition-colors"
                >
                  <button
                    onClick={() => handleToggleSubtask(subtask)}
                    className="mt-0.5 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center -ml-2"
                  >
                    {subtask.is_completed ? (
                      <CheckCircle className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${subtask.is_completed ? 'line-through text-gray-400' : 'text-gray-800 dark:text-white'}`}>
                      {subtask.title}
                    </p>
                    {subtask.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtask.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {subtask.due_date && (
                        <span>Due: {fmt.short(subtask.due_date)}</span>
                      )}
                      {subtask.completed_at && (
                        <span>Completed: {fmt.short(subtask.completed_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">{t('tasks.no_subtasks', 'No subtasks')}</p>
          )}
        </div>
      )}
    </div>
  );
}
